import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { onAuthStateChanged, getAuth, User } from "firebase/auth";

/** `undefined` means "still resolving"; `null` means "signed out". */
type AuthState = User | null | undefined;

const AuthContext = createContext<AuthState>(undefined);

/**
 * One `onAuthStateChanged` subscription for the whole app.
 *
 * Both the layout and the protected route need auth state, and hoisting it here
 * also guarantees `auth.currentUser` is populated before any protected
 * component renders — which is what lets the Firestore helpers read
 * `auth.currentUser` directly instead of wrapping writes in their own listener.
 */
export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthState>(undefined);

  useEffect(
    () => onAuthStateChanged(getAuth(), (user) => setCurrentUser(user)),
    []
  );

  return (
    <AuthContext.Provider value={currentUser}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthState => useContext(AuthContext);

export default useAuth;
